// FloorSlab.ts — PURE floor/ceiling slab builder for the Buildings floor layer (§4.3). Extrudes a ring from `baseZ` to `topZ`: top face, bottom face, edge band. Uses the hand-rolled ear-clipper (`Triangulate_Pure`). Pure: no three / mobx-graphlink, so the Node vitest lane imports it directly.
// Set2 §2 note: walls span `[centerline − t/2, centerline + t/2]`, so a slab over the OUTER ring oversails the wall by t/2 while one over CENTERLINE reaches the wall's middle. Set2 F1 keeps the Set1 behavior (outer ring) so the change is a pure translation; §6.2's "slab to centerline, finish to inner face" split is an F3 deliverable. This builder is convention-agnostic — it just extrudes whatever ring it is given.
import type {Vec2Simple} from "../../Utils/ThreeJS/VectorStructs.js";
import {MeshBuilder, type MeshPlan, type V3} from "./MeshPlan.js";
import {Triangulate2D} from "./Triangulate_Pure.js";

export type FloorSlabOpts = {
	/** The ring the slab is extruded over, CLOSED + CCW. Normally the room's OUTER wall ring
	 *  (Set2 §2: centerline + t/2) so the slab reaches under the walls. */
	outerRing: Vec2Simple[];
	baseZ: number;
	topZ: number;
	topSlot: string;
	bottomSlot: string;
	edgeSlot: string;
};

export type CeilingPlaneOpts = {
	/** The ceiling's footprint ring (CLOSED + CCW) — the room's INTERIOR ring (Set2 §2: the
	 *  centerline inset by t/2), so the downward face stops at the room's real interior face
	 *  instead of poking into the walls. */
	footprint: Vec2Simple[];
	/** z of the ceiling plane, relative to the floor's `floorTopZ` (= the room's `wallTopZ`). */
	z: number;
	slot: string;
};

/** Build a floor slab mesh (top + bottom + edge band) over the `outerRing`. */
export function BuildFloorSlab(opts: FloorSlabOpts): MeshPlan {
	const b = new MeshBuilder();
	const ring = opts.outerRing;
	const baseZ = opts.baseZ, topZ = opts.topZ;
	if (topZ - baseZ < 1e-9 || ring.length < 3) return b.build();

	// --- Top face (normal +z).
	const tris = Triangulate2D(ring);
	if (tris) {
		b.setMaterialSlot(opts.topSlot);
		for (let i = 0; i < tris.length; i += 3) {
			const ia = tris[i], ib = tris[i + 1], ic = tris[i + 2];
			const A = [ring[ia][0], ring[ia][1], topZ] as V3;
			const B = [ring[ib][0], ring[ib][1], topZ] as V3;
			const C = [ring[ic][0], ring[ic][1], topZ] as V3;
			b.tri(A, B, C, [0, 0, 1], [ring[ia][0], ring[ia][1]], [ring[ib][0], ring[ib][1]], [ring[ic][0], ring[ic][1]]);
		}
	}

	// --- Bottom face (normal -z).
	if (tris) {
		b.setMaterialSlot(opts.bottomSlot);
		for (let i = 0; i < tris.length; i += 3) {
			const ia = tris[i], ib = tris[i + 1], ic = tris[i + 2];
			const A = [ring[ia][0], ring[ia][1], baseZ] as V3;
			const B = [ring[ib][0], ring[ib][1], baseZ] as V3;
			const C = [ring[ic][0], ring[ic][1], baseZ] as V3;
			// flip winding so the bottom faces down
			b.tri(C, B, A, [0, 0, -1], [ring[ic][0], ring[ic][1]], [ring[ib][0], ring[ib][1]], [ring[ia][0], ring[ia][1]]);
		}
	}

	// --- Edge band (one quad per outer ring edge), normal = outward.
	b.setMaterialSlot(opts.edgeSlot);
	const n = ring.length;
	for (let i = 0; i < n; i++) {
		const a = ring[i];
		const c = ring[(i + 1) % n];
		const A = [a[0], a[1], baseZ] as V3;
		const B = [c[0], c[1], baseZ] as V3;
		const C = [c[0], c[1], topZ] as V3;
		const D = [a[0], a[1], topZ] as V3;
		// outward normal of edge (a→c) for a CCW ring.
		const dx = c[0] - a[0], dy = c[1] - a[1];
		const len = Math.hypot(dx, dy) || 1;
		const nrm: V3 = [dy / len, -dx / len, 0];
		b.quad(A, B, C, D, nrm, [0, 0], [len, 0], [len, topZ - baseZ], [0, topZ - baseZ]);
	}
	return b.build();
}

/** Build a ZERO-THICKNESS ceiling plane: a single downward-facing surface (normal [0,0,-1]) over
 *  the room's footprint at `z`. This is the top-floor ceiling skin — a thin membrane under the
 *  roof that hides the roof's underside from inside the room. It deliberately emits ONLY the
 *  downward face (no top face, no edge band), because:
 *   - the walls block side view, and
 *   - whatever sits above (the roof surface, or nothing at the top of a roof-less building) is
 *     blocked from the room's interior by this plane,
 *  so the plane is never seen from above or the side and needs no back face. Emitting a single
 *  front-facing surface also means it cannot z-fight with any coplanar surface above.
 *
 *  For interior (non-top) floors a separate ceiling is NOT emitted: the floor slab ABOVE already
 *  provides the ceiling via its `floorUnderside` face (recolored to plaster) — adding a second
 *  coplanar ceiling here would z-fight. So `BuildCeilingPlane` is only called for the top floor. */
export function BuildCeilingPlane(opts: CeilingPlaneOpts): MeshPlan {
	const b = new MeshBuilder();
	const ring = opts.footprint;
	const z = opts.z;
	if (ring.length < 3) return b.build();

	b.setMaterialSlot(opts.slot);
	const tris = Triangulate2D(ring);
	if (tris) {
		const n: V3 = [0, 0, -1]; // downward — faces into the room.
		for (let i = 0; i < tris.length; i += 3) {
			const ia = tris[i], ib = tris[i + 1], ic = tris[i + 2];
			const A = [ring[ia][0], ring[ia][1], z] as V3;
			const B = [ring[ib][0], ring[ib][1], z] as V3;
			const C = [ring[ic][0], ring[ic][1], z] as V3;
			b.tri(A, B, C, n, [ring[ia][0], ring[ia][1]], [ring[ib][0], ring[ib][1]], [ring[ic][0], ring[ic][1]]);
		}
	}
	return b.build();
}
